import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import styles from '../styles/B2C/EmailCheck.module.scss';
import { checkEmail } from '../utils/b2cAuthApi';
import { KcLogo, MailIcon, ArrowRightIcon, CloseIcon } from '../utils/b2cIcons';
import { readB2cPrefs, buildApiPreferences, clearB2cPrefs, arePrefsComplete, getMissingPrefFields, hasVisibleWizardPrefs } from '../utils/b2c/prefs';
import { studentPlatformUrl, signUpUrl } from '../utils/b2c/urls';
import { setB2cUserTypeCookie } from '../utils/b2c/cookies';
import { CF_APP_URL, STORAGE_KEY_B2C_PENDING_EMAIL, isValidEmail } from '../utils/b2c/constants';
import { readSourceContext } from '../utils/b2c/sourceTracking';

export default function EmailCheck() {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const storedPartnerUuid = readB2cPrefs()?.partnerUuid ?? '';

  useEffect(() => {
    if (router.query.from === 'login') {
      clearB2cPrefs();
    }
  }, [router.query.from]);

  // When navigating to /onboarding we intentionally leave loading=true, which
  // makes this page a bfcache snapshot with a frozen spinner. On a back-button
  // restore the JS state is resumed as-is (React never re-mounts), so the loader
  // would spin forever. Reset it whenever the page is shown from bfcache.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setLoading(false);
        setError('');
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const routeByEmailExistence = async (trimmedEmail: string) => {
    setError('');
    setLoading(true);
    try {
      const storedPrefs = readB2cPrefs();
      const apiPrefs = storedPrefs ? buildApiPreferences(storedPrefs) : undefined;
      const prefsPartnerUuid = storedPrefs?.partnerUuid?.trim() || undefined;

      const result = await checkEmail(trimmedEmail, apiPrefs, prefsPartnerUuid);
      if (result.exists) {
        // Set b2cUserType cookie so CF login knows how to redirect after OTP
        setB2cUserTypeCookie(result.userType ?? 'student', new URL(CF_APP_URL).hostname);
        // Existing user — CF login page handles OTP send+verify internally.
        // Read source context but do NOT clear here: a refresh on this page must
        // still see the context, and the next /onboarding visit (or login click)
        // will overwrite it anyway.
        const { sourceUrl: storedSourceUrl, primarySource: storedPrimarySource, fifthStepVisible } = readSourceContext();
        // If the user came through the wizard, signal CF to route them to Explore
        // Universities instead of Home. CF page renders with the user's saved CF prefs
        // (we do NOT forward wizard prefs — saved preferences stay authoritative).
        const cameFromJourney = hasVisibleWizardPrefs(storedPrefs);
        window.location.href = studentPlatformUrl(
          CF_APP_URL,
          result.partnerUuid ?? '',
          result.uuid ?? '',
          'login',
          storedSourceUrl,
          cameFromJourney,
          storedPrimarySource,
          fifthStepVisible,
        );
        return;
      }

      if (storedPrefs && arePrefsComplete(storedPrefs)) {
        // Strict guard: never forward to CF without a valid partnerUuid
        // (arePrefsComplete already requires it; this is defense-in-depth)
        const partnerUuid = storedPrefs.partnerUuid?.trim() ?? '';
        if (!partnerUuid) {
          setError('Your preferences are incomplete. Please complete onboarding to continue.');
          sessionStorage.setItem(STORAGE_KEY_B2C_PENDING_EMAIL, trimmedEmail);
          setTimeout(() => { window.location.href = '/onboarding'; }, 1500);
          return;
        }
        const encodedPrefs = Buffer.from(JSON.stringify(storedPrefs)).toString('base64');
        const { sourceUrl: storedSourceUrl, primarySource: storedPrimarySource, fifthStepVisible } = readSourceContext();
        window.location.href = signUpUrl(CF_APP_URL, partnerUuid, encodedPrefs, trimmedEmail, storedSourceUrl, storedPrimarySource, fifthStepVisible);
        return;
      }

      // No prefs or incomplete — show user-facing reason briefly, then redirect to onboarding
      const missing = getMissingPrefFields(storedPrefs);
      const reason = missing.length
        ? `Please complete: ${missing.join(', ')}.`
        : 'Please complete your study preferences to continue.';
      setError(reason);
      sessionStorage.setItem(STORAGE_KEY_B2C_PENDING_EMAIL, trimmedEmail);
      // Keep loading=true so submit button stays disabled during the 1.5s message window
      setTimeout(() => { window.location.href = '/onboarding'; }, 1500);
    } catch (err) {
      console.error('[check-email] Email verification failed:', err);
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError('Please enter your email address.');
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    await routeByEmailExistence(trimmedEmail);
  };

  return (
    <div className={styles.page}>
      <style jsx global>{`
        [id^="kiwi-"],
        iframe[id^="kiwi-"] {
          display: none !important;
        }
      `}</style>
      {loading && (
        <div className={styles.loaderOverlay}>
          <div className={styles.spinner} />
        </div>
      )}

      <div className={styles.content}>
        <div className={styles.logo}>
          <KcLogo />
        </div>

        <form className={styles.card} onSubmit={handleSubmit} noValidate>
          <div className={styles.cardHeader}>
            <div className={styles.titleRow}>
              <h1 className={styles.title}>Sign In to continue</h1>
              <button
                type="button"
                className={styles.closeBtn}
                onClick={() => { window.location.href = router.query.from === 'login' ? '/' : '/search'; }}
                aria-label="Close"
              >
                <CloseIcon />
              </button>
            </div>
            <p className={styles.subtitle}>
              Enter email address then you can enter the verification code.
            </p>
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="email">
              Email Address*
            </label>
            <div className={styles.inputWrapper}>
              <input
                id="email"
                type="email"
                inputMode="email"
                autoFocus
                className={styles.input}
                placeholder="Enter Email"
                value={email}
                onChange={(emailEvent) => { setEmail(emailEvent.target.value); if (error) setError(''); }}
                autoComplete="email"
              />
              <MailIcon className={styles.mailIcon} />
            </div>
            {error && <p className={styles.errorText}>{error}</p>}
          </div>

          <button
            type="submit"
            className={styles.button}
            disabled={loading || !isValidEmail(email.trim())}
          >
            <span>{loading ? 'Checking...' : 'Continue'}</span>
            <ArrowRightIcon className={styles.buttonIcon} />
          </button>

          <p className={styles.footer}>
            Don&apos;t have an account?{' '}
            <a
              href={`${CF_APP_URL}/student-platform/${storedPartnerUuid}/sign-up`}
              className={styles.footerLink}
              onClick={(e) => {
                e.preventDefault();
                const trimmedEmail = email.trim();
                if (!trimmedEmail) {
                  setError('Email is mandatory. Please enter your email address.');
                  return;
                }
                if (!isValidEmail(trimmedEmail)) {
                  setError('Please enter a valid email address.');
                  return;
                }
                if (loading) return;
                void routeByEmailExistence(trimmedEmail);
              }}
            >
              Create Account
            </a>
          </p>
        </form>
      </div>

      <div className={styles.bottomIllustration} aria-hidden="true">
        {/* eslint-disable @next/next/no-img-element */}
        <img className={styles.cloudLeft} src="/images/b2c/sign-in/cloud-left.svg" alt="" />
        <img className={styles.cloudRight} src="/images/b2c/sign-in/cloud-right.svg" alt="" />
        <img className={styles.towerLeft} src="/images/b2c/sign-in/tower-left.svg" alt="" />
        <img className={styles.buildingTallLeft} src="/images/b2c/sign-in/building-tall-left.svg" alt="" />
        <img className={styles.buildingGrid} src="/images/b2c/sign-in/building-grid.svg" alt="" />
        <img className={styles.ferrisWheel} src="/images/b2c/sign-in/ferris-wheel.svg" alt="" />
        <img className={styles.temple} src="/images/b2c/sign-in/temple.svg" alt="" />
        <img className={styles.buildingTallRight} src="/images/b2c/sign-in/building-tall-right.svg" alt="" />
        <img className={styles.moon} src="/images/b2c/sign-in/moon.svg" alt="" />
        <img className={styles.towerRight} src="/images/b2c/sign-in/tower-right.svg" alt="" />
        <img className={styles.buildingWithDoor} src="/images/b2c/sign-in/building-with-door.svg" alt="" />
        {/* Mobile-only assets (hidden on desktop, shown ≤768px) */}
        <img className={styles.mobileSun} src="/images/b2c/sign-in/mobile-sun.svg" alt="" />
        <img className={styles.mobileTemple} src="/images/b2c/sign-in/mobile-temple.svg" alt="" />
        <img className={styles.mobilePagoda} src="/images/b2c/sign-in/mobile-pagoda.svg" alt="" />
        {/* eslint-enable @next/next/no-img-element */}
      </div>
    </div>
  );
}
